import React from 'react';

import type { ImageAsset } from '../../../../../core/data-sources/content-components/types.js';

import { calculatePosition } from '../../../hooks/useHotspot.js';
import {
  calculateRatio,
  articleSizes,
} from '../../commonComponents/imageUtils.js';

export type ImageProps = {
  articleTitle?: string;
  imageAsset: ImageAsset;
  byline?: {
    title?: string;
  };
  caption?: {
    value: string;
  };
  presentationProperties?: { altText?: string };
  lazyLoad?: boolean;
  hideByline?: boolean;
  fullWidth?: boolean;
  isMobile?: boolean;
};

const Image: React.FC<ImageProps> = (props: ImageProps) => {
  const {
    articleTitle,
    imageAsset,
    byline,
    caption,
    hideByline,
    fullWidth,
    lazyLoad,
    isMobile,
    presentationProperties,
  } = props;
  const width = imageAsset?.size?.width;
  const height = imageAsset?.size?.height;

  const url = getImageUrl(imageAsset.id, width, height, fullWidth, isMobile);

  let hotspotStyles;
  if (imageAsset.hotspot && imageAsset.size) {
    const [x, y] = calculatePosition(imageAsset.hotspot, imageAsset.size);
    hotspotStyles = {
      objectPosition: `${x}% ${y}% !important`,
    };
  }

  const altText =
    presentationProperties?.altText || caption?.value || articleTitle || '';

  const isVerticalImage =
    width && height ? calculateRatio(width, height) < 1 : false;

  const classNames = ['article-image', isVerticalImage ? 'vertical' : ''].join(
    ' ',
  );

  return (
    <div className={classNames}>
      {lazyLoad ? (
        <>
          <noscript>
            <img
              className={`no-script image`}
              src={url}
              alt={altText}
              style={hotspotStyles}
            />
          </noscript>
          <img
            className={`b-lazy image`}
            src={url}
            alt={altText}
            loading="lazy"
            decoding="async"
            style={hotspotStyles}
          />
        </>
      ) : (
        <img
          src={url}
          alt={altText}
          loading="eager"
          decoding="auto"
          style={hotspotStyles}
        />
      )}
      {!hideByline && (
        <div className="image-info">
          {caption && <span className="italic">{caption.value}</span>}
          {byline && <span className="italic"> Foto: {byline.title} </span>}
        </div>
      )}
    </div>
  );
};

export const getImageUrl = (
  id: string,
  width?: number,
  height?: number,
  fullWidth?: boolean,
  isMobile?: boolean,
) => {
  const r = ratio(width, height, fullWidth, isMobile);

  return `https://premium.vgc.no/ap/images/${id}?fit=crop&h=${r.h}&q=80&tight=true&w=${r.w}`;
};

// we'll have to spec these
export const ratio = (
  width?: number,
  height?: number,
  fullWidth?: boolean,
  isMobile?: boolean,
) => {
  if (!width || !height) {
    return articleSizes(null, !!isMobile, !!fullWidth);
  }

  const r = calculateRatio(width, height);

  return articleSizes(r, !!isMobile, !!fullWidth);
};

export default Image;
